/**
 * Where the SPA runs decides what it is (ADR-0002/D-1, identity contract §1, §7):
 * - `{slug}.<root domain>` → the tenant app for tenant `slug`;
 * - the root domain itself (any non-tenant host) → the operator console;
 * - development only: `VITE_DEV_TENANT` turns the root host into a tenant app that sends `X-Positive-Tenant`.
 */
export type HostMode = { kind: "tenant"; slug: string; devTenantHeader: boolean } | { kind: "console" };

export interface HostOptions {
  /** Root domain, e.g. `positive-erp.com` (production) or `localhost` (development). */
  rootDomain: string;
  /** Development-only tenant for the root host (`VITE_DEV_TENANT`), ignored in production builds. */
  devTenant?: string | undefined;
}

const slugPattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/u;

export function isTenantSlug(value: string): boolean {
  return slugPattern.test(value);
}

/** Resolves the tenant from the host name (without port). */
export function resolveHostMode(hostname: string, { rootDomain, devTenant }: HostOptions): HostMode {
  const host = hostname.toLowerCase().replace(/\.$/u, "");
  const root = rootDomain.toLowerCase();
  if (host.endsWith(`.${root}`)) {
    const label = host.slice(0, -(root.length + 1));
    // Only a single label below the root domain names a tenant (no `a.b.root`).
    if (!label.includes(".") && isTenantSlug(label)) return { kind: "tenant", slug: label, devTenantHeader: false };
  }
  if (devTenant && isTenantSlug(devTenant)) return { kind: "tenant", slug: devTenant, devTenantHeader: true };
  return { kind: "console" };
}
